import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Header,
  Pagination,
  Table,
  SpaceBetween,
  StatusIndicator,
  TextFilter
} from '@cloudscape-design/components';

// Helper function to generate timeline cells
const generateTimelineCells = (startDate, duration) => {
  const currentDate = new Date();
  const months = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'];
  
  return months.map(month => {
    const isActive = duration > 0;
    duration -= 1;
    return (
      <Box
        display="inline-block"
        color={isActive ? 'text-status-success' : 'text-status-inactive'}
        textAlign="center"
        padding="xs"
      >
        {isActive ? '======' : '------'}
      </Box>
    );
  });
};

// Dummy data for projects
const projectsData = [
  {
    id: '1',
    projectName: 'ECommerce Platform',
    status: 'In Progress',
    startDate: '2024-04-01',
    duration: 2, // months
    completion: '45%',
    team: 'Team Alpha',
    type: 'in-progress'
  },
  {
    id: '2',
    projectName: 'Banking Portal',
    status: 'In Progress',
    startDate: '2024-04-15',
    duration: 3,
    completion: '30%',
    team: 'Team Beta',
    type: 'in-progress'
  },
  {
    id: '3',
    projectName: 'CRM System',
    status: 'Scheduled',
    startDate: '2024-05-01',
    duration: 3,
    completion: '0%',
    team: 'Team Gamma',
    type: 'pending'
  },
  {
    id: '4',
    projectName: 'HR Portal',
    status: 'Scheduled',
    startDate: '2024-05-15',
    duration: 2,
    completion: '0%',
    team: 'Team Delta',
    type: 'pending'
  }
];

const TimelineView = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const columnDefinitions = [
    {
      id: 'projectName',
      header: 'Project Name',
      cell: item => item.projectName,
      sortingField: 'projectName'
    },
    {
      id: 'status',
      header: 'Status',
      cell: item => (
        <StatusIndicator type={item.type}>
          {item.status}
        </StatusIndicator>
      ),
      sortingField: 'status'
    },
    {
      id: 'timeline',
      header: 'Timeline',
      cell: item => (
        <Box display="flex" gap="xs">
          {generateTimelineCells(item.startDate, item.duration)}
        </Box>
      )
    },
    {
      id: 'completion',
      header: 'Completion',
      cell: item => item.completion,
      sortingField: 'completion'
    },
    {
      id: 'team',
      header: 'Team',
      cell: item => item.team,
      sortingField: 'team'
    }
  ];

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Timeline view of ongoing and upcoming modernization projects"
          actions={
            <Button variant="primary">
              Add Project
            </Button>
          }
        >
          Project Timeline
        </Header>
      }
    >
      <SpaceBetween size="m">
        {/* Legend */}
        <Box padding="s">
          <SpaceBetween size="xs" direction="horizontal">
            <StatusIndicator type="in-progress">In Progress</StatusIndicator>
            <StatusIndicator type="pending">Scheduled</StatusIndicator>
            <Box color="text-status-inactive">Not Started</Box>
          </SpaceBetween>
        </Box>

        <Table
          columnDefinitions={columnDefinitions}
          items={projectsData}
          loadingText="Loading projects"
          selectionType="multi"
          selectedItems={selectedItems}
          onSelectionChange={({ detail }) => 
            setSelectedItems(detail.selectedItems)
          }
          filter={
            <TextFilter
              filteringPlaceholder="Find projects"
              filteringText={filterText}
              onChange={({ detail }) => setFilterText(detail.filteringText)}
            />
          }
          pagination={
            <Pagination
              currentPageIndex={currentPage}
              onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
              pagesCount={1}
            />
          }
          variant="container"
          stickyHeader
          stripedRows
        />

        {/* Summary Section */}
        <Box padding="s">
          <SpaceBetween size="m">
            <Box variant="awsui-key-label">
              Total Projects: {projectsData.length}
            </Box>
            <Box variant="awsui-key-label">
              Active Projects: {projectsData.filter(p => p.type === 'in-progress').length}
            </Box>
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </Container>
  );
};

export default TimelineView;
